import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Heart,
  MagnifyingGlass,
  Trash,
  VideoCamera,
  Brain,
  BookOpen,
  ChatCircle,
  ClipboardText,
  ShieldWarning,
  Lightbulb,
  Question,
  X,
} from "@phosphor-icons/react";
import { useLang } from "../contexts/LanguageContext";
import { API_BASE as API } from "../lib/apiBase";

/**
 * Vincent B877 (Eric 2026-02-14 SPEC STRICK PROD) — Page « Mes favoris »
 * globale (toutes vidéos confondues), listée dans le menu Apprentissage.
 *
 * Complète l'onglet « Mes favoris » per-vidéo introduit en B849 en
 * offrant une vue transverse de TOUS les ♥ de l'utilisateur :
 *   - Recherche plein-texte sur le titre.
 *   - Filtres par type (Tous, Vidéo, Q&R, Concept, Objection, Procédure,
 *     Script de vente, Synthèse, Dictionnaire).
 *   - Chaque item ouvre la source native (vidéo à l'onglet+timecode
 *     approprié, ou fallback vers /dashboard/knowledge?tab=...&focus=).
 *
 * Réutilise le mapping URL de DashboardPage (source unique de vérité
 * pour ne pas dupliquer la logique de routage per subtype/video_id).
 */

// Sous-types de connaissance → libellé FR (Vincent B583).
const KB_SUBTYPE_LABELS = {
  "qa": "Q&R",
  "q&r": "Q&R",
  "qr": "Q&R",
  "question": "Q&R",
  "objection": "Objection",
  "rebuttal": "Objection",
  "procedure": "Procédure",
  "procédure": "Procédure",
  "concept": "Concept",
  "script": "Script de vente",
  "sales-script": "Script de vente",
  "sales_script": "Script de vente",
  "synthesis": "Synthèse",
};

// Sous-type → onglet VideoDetailPage (activePanel), Vincent B590/B621.
const KB_SUBTYPE_VIDEO_TABS = {
  "qa": "qa",
  "q&r": "qa",
  "qr": "qa",
  "question": "qa",
  "objection": "objections",
  "rebuttal": "objections",
  "procedure": "procedures",
  "procédure": "procedures",
  "concept": "knowledge",
  "script": "scripts",
  "sales-script": "scripts",
  "sales_script": "scripts",
  "synthesis": "synthesis",
};

// Sous-type → onglet KnowledgePage (activeTab, fallback sans vidéo source).
const KB_SUBTYPE_KB_TABS = {
  "qa": "qa",
  "q&r": "qa",
  "qr": "qa",
  "question": "qa",
  "objection": "objection",
  "rebuttal": "rebuttal",
  "procedure": "procedure",
  "procédure": "procedure",
  "concept": "concept",
  "script": "script",
  "sales-script": "script",
  "sales_script": "script",
  "synthesis": "synthesis",
};

// Filtres proposés à l'utilisateur.
const FILTERS = [
  { id: "all", labelFr: "Tous", labelEn: "All", icon: Heart, matcher: () => true },
  {
    id: "video",
    labelFr: "Vidéos",
    labelEn: "Videos",
    icon: VideoCamera,
    matcher: (f) => f.item_type === "video",
  },
  {
    id: "synthesis",
    labelFr: "Synthèses",
    labelEn: "Syntheses",
    icon: BookOpen,
    matcher: (f) => f.item_type === "knowledge" && (f.knowledge_type || "").toLowerCase() === "synthesis",
  },
  {
    id: "qa",
    labelFr: "Q&R",
    labelEn: "FAQ",
    icon: Question,
    matcher: (f) => f.item_type === "knowledge" && ["qa", "q&r", "qr", "question"].includes((f.knowledge_type || "").toLowerCase()),
  },
  {
    id: "concept",
    labelFr: "Concepts",
    labelEn: "Concepts",
    icon: Lightbulb,
    matcher: (f) => f.item_type === "knowledge" && (f.knowledge_type || "").toLowerCase() === "concept",
  },
  {
    id: "procedure",
    labelFr: "Procédures",
    labelEn: "Procedures",
    icon: ClipboardText,
    matcher: (f) => f.item_type === "knowledge" && ["procedure", "procédure"].includes((f.knowledge_type || "").toLowerCase()),
  },
  {
    id: "script",
    labelFr: "Scripts de vente",
    labelEn: "Sales scripts",
    icon: ChatCircle,
    matcher: (f) =>
      f.item_type === "sales_script" ||
      (f.item_type === "knowledge" && ["script", "sales-script", "sales_script"].includes((f.knowledge_type || "").toLowerCase())),
  },
  {
    id: "objection",
    labelFr: "Objections",
    labelEn: "Objections",
    icon: ShieldWarning,
    matcher: (f) => f.item_type === "knowledge" && ["objection", "rebuttal"].includes((f.knowledge_type || "").toLowerCase()),
  },
  {
    id: "dictionary_term",
    labelFr: "Dictionnaire",
    labelEn: "Dictionary",
    icon: BookOpen,
    matcher: (f) => f.item_type === "dictionary_term",
  },
];

function buildHref(fav) {
  if (fav.item_type === "video") {
    return `/dashboard/video/${fav.item_id}`;
  }
  if (fav.item_type === "sales_script") {
    return fav.video_id
      ? `/dashboard/video/${fav.video_id}?tab=scripts&focus=${fav.item_id}`
      : `/dashboard/knowledge?tab=script&focus=${fav.item_id}`;
  }
  if (fav.item_type === "dictionary_term") {
    return `/dashboard/dictionary?focus=${fav.item_id}`;
  }
  if (fav.item_type === "knowledge") {
    const subtype = (fav.knowledge_type || "").toLowerCase();
    const videoTab = KB_SUBTYPE_VIDEO_TABS[subtype];
    const kbTab = KB_SUBTYPE_KB_TABS[subtype];
    if (fav.video_id && videoTab) {
      return `/dashboard/video/${fav.video_id}?tab=${videoTab}&focus=${fav.item_id}`;
    }
    if (kbTab) {
      return `/dashboard/knowledge?tab=${kbTab}&focus=${fav.item_id}`;
    }
    return `/dashboard/knowledge?focus=${fav.item_id}`;
  }
  return "/dashboard/knowledge";
}

function typeLabelFor(fav, lang) {
  if (fav.item_type === "video") return lang === "en" ? "Video" : "Vidéo";
  if (fav.item_type === "sales_script") return lang === "en" ? "Sales script" : "Script de vente";
  if (fav.item_type === "dictionary_term") return lang === "en" ? "Dictionary term" : "Mot du dictionnaire";
  if (fav.item_type === "knowledge") {
    const st = (fav.knowledge_type || "").toLowerCase();
    return KB_SUBTYPE_LABELS[st] || st || "—";
  }
  return fav.item_type || "—";
}

function IconForFav({ fav }) {
  if (fav.item_type === "video") {
    return (
      <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 bg-blue-500/10">
        <VideoCamera size={18} className="text-blue-600" />
      </div>
    );
  }
  if (fav.item_type === "sales_script") {
    return (
      <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 bg-yellow-500/10">
        <ChatCircle size={18} className="text-yellow-800" />
      </div>
    );
  }
  if (fav.item_type === "dictionary_term") {
    return (
      <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 bg-zinc-500/10">
        <BookOpen size={18} className="text-zinc-700" />
      </div>
    );
  }
  return (
    <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 bg-teal-500/10">
      <Brain size={18} className="text-teal-600" />
    </div>
  );
}

// Vincent B988 — Rendu d'une ligne favori (extrait pour être réutilisé
// entre la vue plate et la vue groupée par vidéo).
function renderFavRow(fav, lang, remove) {
  const href = buildHref(fav);
  const typeLabel = typeLabelFor(fav, lang);
  return (
    <li key={`${fav.item_type}-${fav.item_id}`} className="group" data-testid={`my-favorite-row-${fav.id}`}>
      <div className="flex items-center gap-3 px-4 py-3 hover:bg-zinc-50 transition-colors">
        <Link to={href} className="flex items-center gap-3 flex-1 min-w-0" data-testid={`my-favorite-link-${fav.id}`}>
          <IconForFav fav={fav} />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-zinc-900 truncate">
              {fav.title || (lang === "en" ? "Untitled" : "Sans titre")}
            </p>
            <p className="text-xs text-zinc-600 flex items-center gap-2" data-testid={`my-favorite-${fav.id}-type`}>
              <span>{typeLabel}</span>
              {fav.video_title && fav.item_type !== "video" && (
                <>
                  <span className="text-zinc-400">·</span>
                  <span className="truncate text-zinc-500" data-testid={`my-favorite-${fav.id}-video-title`}>
                    {lang === "en" ? "from " : "de "}<span className="italic">{fav.video_title}</span>
                  </span>
                </>
              )}
            </p>
          </div>
        </Link>
        <button
          type="button"
          onClick={() => remove(fav)}
          className="opacity-0 group-hover:opacity-100 focus:opacity-100 p-2 text-zinc-500 hover:text-rose-600 transition-all rounded"
          data-testid={`my-favorite-remove-${fav.id}`}
          aria-label={lang === "en" ? "Remove favorite" : "Retirer le favori"}
          title={lang === "en" ? "Remove favorite" : "Retirer le favori"}
        >
          <Trash size={16} />
        </button>
      </div>
    </li>
  );
}

export default function MyFavoritesPage() {
  const { lang } = useLang();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  // Vincent B988 (Eric 2026-02-26 SPEC STRICK PROD) — Toggle « Grouper par
  // vidéo » : agrège les favoris par vidéo source (via video_title enrichi
  // par le backend). Persistance localStorage pour respecter le choix de
  // l'apprenant entre les visites.
  const [groupByVideo, setGroupByVideo] = useState(() => {
    try { return localStorage.getItem("knoria.favorites.groupByVideo") === "1"; }
    catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("knoria.favorites.groupByVideo", groupByVideo ? "1" : "0"); }
    catch { /* silent */ }
  }, [groupByVideo]);

  const loadFavorites = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API}/api/favorites`, { withCredentials: true });
      setFavorites(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      setError(e?.response?.data?.detail || (lang === "en" ? "Loading error" : "Erreur de chargement"));
    } finally {
      setLoading(false);
    }
  }, [lang]);

  useEffect(() => {
    loadFavorites();
    const onUpdate = () => loadFavorites();
    window.addEventListener("favorites:updated", onUpdate);
    return () => window.removeEventListener("favorites:updated", onUpdate);
  }, [loadFavorites]);

  const remove = async (fav) => {
    try {
      await axios.delete(`${API}/api/favorites/${fav.item_type}/${fav.item_id}`, { withCredentials: true });
      setFavorites((prev) => prev.filter((f) => !(f.item_type === fav.item_type && f.item_id === fav.item_id)));
      window.dispatchEvent(new Event("favorites:updated"));
    } catch {
      /* silent */
    }
  };

  const filtered = useMemo(() => {
    const flt = FILTERS.find((f) => f.id === activeFilter) || FILTERS[0];
    const q = search.trim().toLowerCase();
    return favorites
      .filter(flt.matcher)
      .filter((f) => (q ? (f.title || "").toLowerCase().includes(q) || (f.video_title || "").toLowerCase().includes(q) : true));
  }, [favorites, activeFilter, search]);

  // Vincent B988 — Groupement par vidéo source. Chaque item est mis dans
  // le bucket correspondant à sa video_title (fallback bucket « (aucune) »
  // pour les items sans vidéo source, ex : dictionary_term). Les buckets
  // sont triés par count DESC puis titre pour un affichage stable.
  const groupedByVideo = useMemo(() => {
    const buckets = new Map();
    for (const fav of filtered) {
      const key = fav.video_title || fav.video_id || (lang === "en" ? "(no source video)" : "(sans vidéo source)");
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key).push(fav);
    }
    return Array.from(buckets.entries())
      .map(([videoTitle, items]) => ({ videoTitle, items, videoId: items[0]?.video_id || null }))
      .sort((a, b) => b.items.length - a.items.length || a.videoTitle.localeCompare(b.videoTitle));
  }, [filtered, lang]);

  const countByFilter = useMemo(() => {
    const out = {};
    for (const f of FILTERS) {
      out[f.id] = favorites.filter(f.matcher).length;
    }
    return out;
  }, [favorites]);

  return (
    <div className="max-w-5xl mx-auto py-8 px-4 sm:px-6" data-testid="my-favorites-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-rose-100 flex items-center justify-center">
          <Heart size={22} weight="fill" className="text-rose-600" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold text-zinc-900" data-testid="my-favorites-title">
            {lang === "en" ? "My favorites" : "Mes favoris"}
          </h1>
          <p className="text-sm text-zinc-600">
            {lang === "en"
              ? "All items you have marked with ♥ across every video and knowledge type."
              : "Tout ce que vous avez marqué avec ♥ sur toutes les vidéos et types de connaissances."}
          </p>
        </div>
        <span
          className="ml-auto text-sm font-medium text-zinc-700 bg-zinc-100 border border-zinc-200 rounded-full px-3 py-1"
          data-testid="my-favorites-total-count"
        >
          {favorites.length}
        </span>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={lang === "en" ? "Search by title…" : "Rechercher par titre…"}
          className="w-full pl-10 pr-10 py-2.5 text-sm border border-zinc-300 rounded-lg bg-white text-zinc-900 placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary"
          data-testid="my-favorites-search-input"
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-800"
            data-testid="my-favorites-search-clear"
            aria-label={lang === "en" ? "Clear search" : "Effacer la recherche"}
          >
            <X size={16} />
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-3" data-testid="my-favorites-filters">
        {FILTERS.map((f) => {
          const Icon = f.icon;
          const active = activeFilter === f.id;
          return (
            <button
              key={f.id}
              type="button"
              onClick={() => setActiveFilter(f.id)}
              className={`inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full border transition-colors ${
                active
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-50"
              }`}
              data-testid={`my-favorites-filter-${f.id}`}
              aria-pressed={active}
            >
              <Icon size={14} weight={active ? "fill" : "regular"} />
              {lang === "en" ? f.labelEn : f.labelFr}
              <span
                className={`ml-1 text-[11px] font-semibold rounded-full px-1.5 ${
                  active ? "bg-white/25" : "bg-zinc-100 text-zinc-700"
                }`}
                data-testid={`my-favorites-filter-${f.id}-count`}
              >
                {countByFilter[f.id] ?? 0}
              </span>
            </button>
          );
        })}
      </div>

      {/* Vincent B988 — Toggle groupement par vidéo source. */}
      <div className="flex items-center justify-end mb-3">
        <label className="inline-flex items-center gap-2 text-xs text-zinc-700 cursor-pointer select-none" data-testid="my-favorites-group-toggle-label">
          <input
            type="checkbox"
            checked={groupByVideo}
            onChange={(e) => setGroupByVideo(e.target.checked)}
            className="h-4 w-4 accent-primary"
            data-testid="my-favorites-group-toggle"
          />
          <span>{lang === "en" ? "Group by source video" : "Grouper par vidéo source"}</span>
        </label>
      </div>

      {/* List */}
      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-zinc-600 text-sm" data-testid="my-favorites-loading">
            {lang === "en" ? "Loading…" : "Chargement…"}
          </div>
        ) : error ? (
          <div className="p-6 text-center text-rose-700 bg-rose-50 text-sm" data-testid="my-favorites-error">
            {error}
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-zinc-600" data-testid="my-favorites-empty">
            <Heart size={32} className="mx-auto mb-3 opacity-30" />
            <p className="text-sm">
              {favorites.length === 0
                ? lang === "en"
                  ? "You haven't marked any favorites yet. Tap the ♥ on any knowledge unit, video or script to add it here."
                  : "Vous n'avez encore aucun favori. Touchez le ♥ sur n'importe quelle connaissance, vidéo ou script pour l'ajouter ici."
                : lang === "en"
                  ? "No favorite matches your search / filter."
                  : "Aucun favori ne correspond à votre recherche ou filtre."}
            </p>
          </div>
        ) : (
          groupByVideo ? (
            <div data-testid="my-favorites-grouped-list">
              {groupedByVideo.map((group) => (
                <div key={group.videoTitle} className="border-t border-zinc-200 first:border-t-0" data-testid="my-favorites-group">
                  <div className="px-4 py-2 bg-zinc-50 flex items-center gap-2">
                    <VideoCamera size={14} className="text-zinc-600" />
                    {group.videoId ? (
                      <Link
                        to={`/dashboard/video/${group.videoId}`}
                        className="text-sm font-semibold text-zinc-800 hover:text-primary truncate"
                        data-testid="my-favorites-group-video-link"
                      >
                        {group.videoTitle}
                      </Link>
                    ) : (
                      <span className="text-sm font-semibold text-zinc-800 truncate" data-testid="my-favorites-group-video-title">
                        {group.videoTitle}
                      </span>
                    )}
                    <span className="ml-auto text-[11px] font-semibold rounded-full bg-zinc-200 text-zinc-700 px-2 py-0.5" data-testid="my-favorites-group-count">
                      {group.items.length}
                    </span>
                  </div>
                  <ul className="divide-y divide-zinc-200">
                    {group.items.map((fav) => renderFavRow(fav, lang, remove))}
                  </ul>
                </div>
              ))}
            </div>
          ) : (
            <ul className="divide-y divide-zinc-200" data-testid="my-favorites-list">
              {filtered.map((fav) => renderFavRow(fav, lang, remove))}
            </ul>
          )
        )}
      </div>
    </div>
  );
}
